import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { EmailTemplateEntity, GetEmailTemplatesRequest } from "../types/emailTemplate.type";
import { ENV } from "@app/constants/env";
import { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { getToken } from '@app/helpers/authProvider';

const rawBaseQuery = fetchBaseQuery({
  baseUrl: ENV.BASE_API_URL +ENV.TEMPLATES_APIURL,
});

const authBaseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  incomingArgs,
  api,
  extraOptions
) => {
  let args: string | FetchArgs = incomingArgs;

  // normalize string arg to object so we can set headers
  if (typeof args === 'string') {
    args = { url: args };
  }

  try {
    const token = await getToken();
    if (token) {
      const fetchArgs = args as FetchArgs;
      const existingHeaders = fetchArgs.headers || {};
      const headers = existingHeaders instanceof Headers
        ? existingHeaders
        : new Headers(existingHeaders as HeadersInit);

      headers.set('Authorization', `Bearer ${token}`);
      fetchArgs.headers = headers;
    }

    const result = await rawBaseQuery(args, api, extraOptions);
    return result;
  } catch (error) {
    console.error('Error in authBaseQuery:', error);
    return {
      error: {
        status: 'CUSTOM_ERROR',
        error: String(error),
      } as FetchBaseQueryError,
    };
  }
};

export const emailTemplateApi = createApi({
  reducerPath: 'emailTemplateApi',
  baseQuery: authBaseQuery,
  tagTypes: ['EmailTemplate'],
  endpoints: (builder) => ({
    getEmoEmailTemplates: builder.query<EmailTemplateEntity[], GetEmailTemplatesRequest>({
      query: ({ pageNumber = 1, pageSize = 25, filteredColumn = '' } = {}) => ({
        url: `EmoTemplate/GetEmoEmailTemplates`,
        params: {
          pageNumber,
          pageSize,
          filteredColumn,
        },
      }),
      providesTags: ['EmailTemplate'],
    }),
    deleteEmailTemplate: builder.mutation<void, number>({
      query: (id) => ({
        url: `EmoTemplate/DeleteEmailTemplate/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['EmailTemplate'],
    }),
    getEmailTypes: builder.query<any[], void>({
      query: () => ({
        url: 'EmoTemplate/GetEmailTypes',
      }),
    }),
    getEmailTemplateByTypeId: builder.query<any, number>({
      query: (emailTypeId) => ({
        url: `EmoTemplate/GetEmailTemplateByTypeId/${emailTypeId}`,
      }),
      transformResponse: (response: any) => {
        // If response is an array, return the first item
        if (Array.isArray(response) && response.length > 0) {
          return response[0];
        }
        return response;
      },
    }),
    getEmailTemplateById: builder.query<EmailTemplateEntity, number>({
      query: (id) => ({
        url: `EmoTemplate/GetEmoEmailTemplateById/${id}`,
      }),
      providesTags: ['EmailTemplate'],
    }),
    createUpdateEmailTemplate: builder.mutation<EmailTemplateEntity, Partial<EmailTemplateEntity>>({
      query: (template) => ({
        url: `EmoTemplate/CreateEmailTemplate`,
        method: 'POST',
        body: template,
      }),
      invalidatesTags: ['EmailTemplate'],
    }),
  }),
});

export const {
  useGetEmoEmailTemplatesQuery,
  useDeleteEmailTemplateMutation,
  useGetEmailTypesQuery,
  useGetEmailTemplateByTypeIdQuery,
  useGetEmailTemplateByIdQuery,
  useCreateUpdateEmailTemplateMutation,
} = emailTemplateApi;
